import React, { useState, useRef, useEffect } from 'react';
import {
    MagnifyingGlassIcon,
    BellIcon,
    UserCircleIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
    SunIcon,
    MoonIcon,
    Bars3Icon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import { useAuthContext } from '../context/AuthContext';
import { Badge } from './ui';

interface HeaderProps {
    onMenuToggle?: () => void;
    showMenuButton?: boolean;
}

export default function Header({ onMenuToggle, showMenuButton = false }: HeaderProps) {
    const navigate = useNavigate();
    const { userProfile, signOut } = useAuthContext();
    const [searchQuery, setSearchQuery] = useState('');
    const [showNotifications, setShowNotifications] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);

    const notificationRef = useRef<HTMLDivElement>(null);
    const userMenuRef = useRef<HTMLDivElement>(null);

    // Mock notifications (replace with real data)
    const notifications = [
        {
            id: 1,
            title: 'New Order #12345',
            message: 'A new order has been placed',
            time: '5 minutes ago',
            unread: true,
        },
        {
            id: 2,
            title: 'Low Stock Alert',
            message: 'Product XYZ is running low on stock',
            time: '1 hour ago',
            unread: true,
        },
        {
            id: 3,
            title: 'Payment Received',
            message: 'Payment for order #12344 received',
            time: '2 hours ago',
            unread: false,
        },
    ];

    const unreadCount = notifications.filter(n => n.unread).length;

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check dark mode on mount
    useEffect(() => {
        const isDark = document.documentElement.classList.contains('dark');
        setIsDarkMode(isDark);
    }, []);

    const toggleDarkMode = () => {
        const html = document.documentElement;
        if (html.classList.contains('dark')) {
            html.classList.remove('dark');
            setIsDarkMode(false);
            localStorage.setItem('theme', 'light');
        } else {
            html.classList.add('dark');
            setIsDarkMode(true);
            localStorage.setItem('theme', 'dark');
        }
    };

    const handleLogout = async () => {
        await signOut();
        localStorage.removeItem('isAuthenticated');
        navigate('/login');
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        // Implement search functionality
        console.log('Searching for:', searchQuery);
    };

    return (
        <header className={cn(
            'sticky top-0 z-30',
            'bg-white dark:bg-[#1e293b]',
            'border-b border-gray-200 dark:border-gray-700/50',
            'shadow-sm dark:shadow-black/10'
        )}>
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                {/* Left Section - Menu Button & Search */}
                <div className="flex items-center gap-4 flex-1">
                    {/* Mobile Menu Toggle */}
                    {showMenuButton && (
                        <button
                            onClick={onMenuToggle}
                            className={cn(
                                'p-2 rounded-lg',
                                'text-gray-600 dark:text-gray-400',
                                'hover:bg-gray-100 dark:hover:bg-gray-800',
                                'transition-colors duration-200',
                                'lg:hidden'
                            )}
                        >
                            <Bars3Icon className="w-6 h-6" />
                        </button>
                    )}

                    {/* Search bar removed per spec — search is now
                        scoped to individual list pages instead of a
                        global header field. */}
                    <div className="flex-1" />
                </div>

                {/* Right Section - Actions */}
                <div className="flex items-center gap-2">
                    {/* Dark Mode Toggle */}
                    <button
                        onClick={toggleDarkMode}
                        className={cn(
                            'p-2 rounded-lg',
                            'text-gray-600 dark:text-gray-400',
                            'hover:bg-gray-100 dark:hover:bg-gray-800',
                            'transition-colors duration-200'
                        )}
                        aria-label="Toggle dark mode"
                    >
                        {isDarkMode ? (
                            <SunIcon className="w-5 h-5" />
                        ) : (
                            <MoonIcon className="w-5 h-5" />
                        )}
                    </button>

                    {/* Notifications */}
                    <div className="relative" ref={notificationRef}>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className={cn(
                                'relative p-2 rounded-lg',
                                'text-gray-600 dark:text-gray-400',
                                'hover:bg-gray-100 dark:hover:bg-gray-800',
                                'transition-colors duration-200'
                            )}
                            aria-label="Notifications"
                        >
                            <BellIcon className="w-5 h-5" />
                            {unreadCount > 0 && (
                                <span className="absolute top-1 right-1 w-2 h-2 bg-error-500 rounded-full" />
                            )}
                        </button>

                        {showNotifications && (
                            <div className={cn(
                                'absolute right-0 mt-2 w-80',
                                'bg-white dark:bg-[#1e293b]',
                                'rounded-xl shadow-xl dark:shadow-2xl dark:shadow-black/30',
                                'border border-gray-200 dark:border-gray-600',
                                'overflow-hidden',
                                'animate-fadeInUp'
                            )}>
                                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
                                        {unreadCount > 0 && (
                                            <Badge variant="primary" size="sm">{unreadCount} new</Badge>
                                        )}
                                    </div>
                                </div>
                                <div className="max-h-96 overflow-y-auto custom-scrollbar">
                                    {notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            className={cn(
                                                'px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0',
                                                'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                                                'cursor-pointer transition-colors',
                                                notification.unread && 'bg-primary-50/50 dark:bg-primary-950/50'
                                            )}
                                        >
                                            <div className="flex items-start gap-3">
                                                {notification.unread && (
                                                    <div className="w-2 h-2 mt-2 bg-primary-500 rounded-full flex-shrink-0" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {notification.title}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                        {notification.time}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                                    <button className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                                        View all notifications
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* User Menu */}
                    <div className="relative" ref={userMenuRef}>
                        <button
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className={cn(
                                'flex items-center gap-2 p-2 rounded-lg',
                                'text-gray-700 dark:text-gray-300',
                                'hover:bg-gray-100 dark:hover:bg-gray-800',
                                'transition-colors duration-200'
                            )}
                        >
                            <UserCircleIcon className="w-6 h-6" />
                            <span className="hidden sm:block text-sm font-medium">
                                {userProfile?.name || 'User'}
                            </span>
                        </button>

                        {showUserMenu && (
                            <div className={cn(
                                'absolute right-0 mt-2 w-56',
                                'bg-white dark:bg-[#1e293b]',
                                'rounded-xl shadow-xl dark:shadow-2xl dark:shadow-black/30',
                                'border border-gray-200 dark:border-gray-600',
                                'overflow-hidden',
                                'animate-fadeInUp'
                            )}>
                                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {userProfile?.name || 'User'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {userProfile?.email || 'user@example.com'}
                                    </p>
                                    <Badge variant="primary" size="xs" className="mt-2">
                                        {userProfile?.role || 'User'}
                                    </Badge>
                                </div>
                                <div className="py-2">
                                    <button
                                        onClick={() => {
                                            navigate('/settings');
                                            setShowUserMenu(false);
                                        }}
                                        className={cn(
                                            'w-full flex items-center gap-3 px-4 py-2',
                                            'text-sm text-gray-700 dark:text-gray-300',
                                            'hover:bg-gray-100 dark:hover:bg-gray-700',
                                            'transition-colors'
                                        )}
                                    >
                                        <Cog6ToothIcon className="w-5 h-5" />
                                        Settings
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className={cn(
                                            'w-full flex items-center gap-3 px-4 py-2',
                                            'text-sm text-error-600 dark:text-error-400',
                                            'hover:bg-error-50 dark:hover:bg-error-950',
                                            'transition-colors'
                                        )}
                                    >
                                        <ArrowRightOnRectangleIcon className="w-5 h-5" />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
